// scenes/fournisseurs/index.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  InputAdornment,
  Chip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Badge,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  LocalShipping as LocalShippingIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Visibility as VisibilityIcon,
  FilterList as FilterListIcon,
  Clear as ClearIcon,
  Download as DownloadIcon,
  Print as PrintIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { fournisseurService } from 'services/fournisseurService';
import FournisseurForm from './FournisseurForm';

const Fournisseurs = () => {
  const theme = useTheme();
  const [fournisseurs, setFournisseurs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  
  // États pour les modales
  const [openForm, setOpenForm] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedFournisseur, setSelectedFournisseur] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  
  // Menu d'actions
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedActionFournisseur, setSelectedActionFournisseur] = useState(null);

  // Filtres
  const [filters, setFilters] = useState({
    est_actif: null,
  });

  // Charger les fournisseurs
  const loadFournisseurs = async () => {
    try {
      setLoading(true);
      setError('');
      
      const params = {
        page: page + 1,
        page_size: rowsPerPage,
        search: searchTerm || undefined,
        ...(filters.est_actif !== null && { est_actif: filters.est_actif }),
      };

      const response = await fournisseurService.getAllFournisseurs(params);
      
      // Adaptez selon la structure de votre réponse
      if (response.results) {
        setFournisseurs(response.results);
        setTotalItems(response.count || 0);
      } else {
        setFournisseurs(response);
        setTotalItems(response.length);
      }
    } catch (err) {
      console.error('Erreur de chargement:', err);
      setError(err.message || 'Erreur lors du chargement des fournisseurs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadFournisseurs();
  }, [page, rowsPerPage, searchTerm, filters]);

  // Gestion de la recherche
  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const clearSearch = () => {
    setSearchTerm('');
    setPage(0);
  };

  // Gestion de la pagination
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Gestion des filtres
  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: value,
    }));
    setPage(0);
  };

  const clearFilters = () => {
    setFilters({
      est_actif: null,
    });
    setPage(0);
  };

  // Gestion du formulaire
  const handleOpenForm = (fournisseur = null) => {
    setSelectedFournisseur(fournisseur);
    setIsEditing(!!fournisseur);
    setOpenForm(true);
  };

  const handleCloseForm = () => {
    setOpenForm(false);
    setSelectedFournisseur(null);
  };

  const handleSaveSuccess = () => {
    handleCloseForm();
    loadFournisseurs();
  };

  // Gestion de la suppression
  const handleOpenDeleteDialog = (fournisseur) => {
    setSelectedFournisseur(fournisseur);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setSelectedFournisseur(null);
  };

  const handleDelete = async () => {
    try {
      await fournisseurService.deleteFournisseur(selectedFournisseur.id);
      handleCloseDeleteDialog();
      loadFournisseurs();
    } catch (err) {
      console.error('Erreur de suppression:', err);
      setError(err.message || 'Erreur lors de la suppression');
    }
  };

  // Gestion du menu d'actions
  const handleActionMenuOpen = (event, fournisseur) => {
    setAnchorEl(event.currentTarget);
    setSelectedActionFournisseur(fournisseur);
  };

  const handleActionMenuClose = () => {
    setAnchorEl(null);
    setSelectedActionFournisseur(null);
  };

  const handleToggleActivation = async (fournisseur) => {
    try {
      const newStatus = !fournisseur.est_actif;
      await fournisseurService.toggleActivation(fournisseur.id, newStatus);
      handleActionMenuClose();
      loadFournisseurs();
    } catch (err) {
      console.error('Erreur de changement de statut:', err);
      setError(err.message || 'Erreur lors du changement de statut');
    }
  };

  // Formatage de la date
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  // Formatage du téléphone
  const formatPhone = (phone) => {
    if (!phone) return '-';
    // Format français : 01 23 45 67 89
    return phone.replace(/(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})/, '$1 $2 $3 $4 $5');
  };

  // Statistiques
  const stats = {
    total: totalItems,
    actifs: fournisseurs.filter(f => f.est_actif).length,
    inactifs: fournisseurs.filter(f => !f.est_actif).length,
  };

  return (
    <Box m="20px">
      {/* En-tête */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Box display="flex" alignItems="center" gap={2}>
          <LocalShippingIcon sx={{ fontSize: 40, color: theme.palette.primary.main }} />
          <Box>
            <Typography variant="h3" fontWeight="bold">
              Gestion des Fournisseurs
            </Typography>
            <Typography variant="body1" color="textSecondary">
              Gérez les fournisseurs de votre entreprise
            </Typography>
          </Box>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenForm()}
          sx={{
            backgroundColor: theme.palette.primary.main,
            '&:hover': {
              backgroundColor: theme.palette.primary.dark,
            },
          }}
        >
          Nouveau Fournisseur
        </Button>
      </Box>

      {/* Barre de recherche et filtres */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
          <TextField
            variant="outlined"
            placeholder="Rechercher par nom, téléphone, email..."
            value={searchTerm}
            onChange={handleSearch}
            sx={{ flex: 1, minWidth: 300 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton onClick={clearSearch} size="small">
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          
          <Box display="flex" gap={1} alignItems="center">
            <Tooltip title="Filtrer par statut">
              <Button
                variant="outlined"
                startIcon={<FilterListIcon />}
                onClick={() => handleFilterChange('est_actif', true)}
                color={filters.est_actif === true ? 'primary' : 'inherit'}
              >
                Actifs
              </Button>
            </Tooltip>
            
            <Tooltip title="Filtrer par inactifs">
              <Button
                variant="outlined"
                startIcon={<FilterListIcon />}
                onClick={() => handleFilterChange('est_actif', false)}
                color={filters.est_actif === false ? 'primary' : 'inherit'}
              >
                Inactifs
              </Button>
            </Tooltip>
            
            {(filters.est_actif !== null) && (
              <Tooltip title="Effacer les filtres">
                <IconButton onClick={clearFilters} size="small">
                  <ClearIcon />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>
      </Paper>

      {/* Statistiques */}
      <Box display="flex" gap={3} mb={3}>
        <Paper sx={{ p: 3, flex: 1 }}>
          <Typography variant="h6" color="textSecondary" gutterBottom>
            Total Fournisseurs
          </Typography>
          <Typography variant="h3" fontWeight="bold">
            {stats.total}
          </Typography>
        </Paper>
        <Paper sx={{ p: 3, flex: 1 }}>
          <Typography variant="h6" color="textSecondary" gutterBottom>
            Actifs
          </Typography>
          <Typography variant="h3" fontWeight="bold" color="success.main">
            {stats.actifs}
          </Typography>
        </Paper>
        <Paper sx={{ p: 3, flex: 1 }}>
          <Typography variant="h6" color="textSecondary" gutterBottom>
            Inactifs
          </Typography>
          <Typography variant="h3" fontWeight="bold" color="error.main">
            {stats.inactifs}
          </Typography>
        </Paper>
      </Box>

      {/* Message d'erreur */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Tableau des fournisseurs */}
      <Paper sx={{ width: '100%', overflow: 'hidden', mb: 4 }}>
        <TableContainer sx={{ maxHeight: 600 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Statut</TableCell>
                <TableCell>Nom</TableCell>
                <TableCell>Contact</TableCell>
                <TableCell>Adresse</TableCell>
                <TableCell>Date création</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                    <CircularProgress />
                    <Typography variant="body2" sx={{ mt: 2 }}>
                      Chargement des fournisseurs...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : fournisseurs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                    <LocalShippingIcon sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
                    <Typography variant="h6" color="textSecondary">
                      Aucun fournisseur trouvé
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                      {searchTerm || filters.est_actif !== null 
                        ? 'Aucun résultat pour votre recherche' 
                        : ''
                      }
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                fournisseurs.map((fournisseur) => (
                  <TableRow key={fournisseur.id} hover>
                    <TableCell>
                      <Chip
                        icon={fournisseur.est_actif ? <CheckCircleIcon /> : <CancelIcon />}
                        label={fournisseur.est_actif ? 'Actif' : 'Inactif'}
                        color={fournisseur.est_actif ? 'success' : 'error'}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography fontWeight="medium">{fournisseur.nom}</Typography>
                        {fournisseur.email && (
                          <Typography variant="body2" color="textSecondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <EmailIcon fontSize="small" />
                            {fournisseur.email}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box>
                        {fournisseur.contact_personne && (
                          <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                            <PersonIcon fontSize="small" />
                            {fournisseur.contact_personne}
                          </Typography>
                        )}
                        {fournisseur.telephone && (
                          <Typography variant="body2" color="textSecondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <PhoneIcon fontSize="small" />
                            {formatPhone(fournisseur.telephone)}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        color="textSecondary"
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 0.5,
                          maxWidth: 200,
                        }}
                      >
                        <LocationIcon fontSize="small" sx={{ mt: 0.25 }} />
                        <span>
                          {fournisseur.adresse || 'Non renseignée'}
                          {fournisseur.ville && `, ${fournisseur.ville}`}
                        </span>
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(fournisseur.date_creation)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Box display="flex" justifyContent="center" gap={1}>
                        <Tooltip title="Modifier">
                          <IconButton
                            color="primary"
                            onClick={() => handleOpenForm(fournisseur)}
                            size="small"
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        
                        <Tooltip title="Plus d'options">
                          <IconButton
                            onClick={(e) => handleActionMenuOpen(e, fournisseur)}
                            size="small"
                          >
                            <MoreVertIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        {/* Pagination */}
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={totalItems}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Lignes par page:"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} sur ${count}`
          }
        />
      </Paper>

      {/* Menu d'actions contextuel */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleActionMenuClose}
      >
        {selectedActionFournisseur && (
          <>
            <MenuItem onClick={() => handleToggleActivation(selectedActionFournisseur)}>
              <ListItemIcon>
                {selectedActionFournisseur.est_actif ? (
                  <CancelIcon fontSize="small" color="error" />
                ) : (
                  <CheckCircleIcon fontSize="small" color="success" />
                )}
              </ListItemIcon>
              <ListItemText>
                {selectedActionFournisseur.est_actif ? 'Désactiver' : 'Activer'}
              </ListItemText>
            </MenuItem>
            
            {/* <MenuItem onClick={() => {
              handleActionMenuClose();
              // Voir les détails ou produits du fournisseur
            }}> */}
              {/* <ListItemIcon>
                <VisibilityIcon fontSize="small" />
              </ListItemIcon> */}
              {/* <ListItemText>Voir détails</ListItemText> */}
            {/* </MenuItem> */}
            
            <MenuItem onClick={() => {
              handleActionMenuClose();
              handleOpenDeleteDialog(selectedActionFournisseur);
            }}>
              <ListItemIcon>
                <DeleteIcon fontSize="small" color="error" />
              </ListItemIcon>
              <ListItemText sx={{ color: 'error.main' }}>Supprimer</ListItemText>
            </MenuItem>
          </>
        )}
      </Menu>

      {/* Modal de formulaire */}
      <FournisseurForm
        open={openForm}
        onClose={handleCloseForm}
        onSuccess={handleSaveSuccess}
        fournisseur={selectedFournisseur}
        isEditing={isEditing}
      />

      {/* Modal de confirmation de suppression */}
      <Dialog
        open={openDeleteDialog}
        onClose={handleCloseDeleteDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          Confirmer la suppression
        </DialogTitle>
        <DialogContent>
          <Typography>
            Êtes-vous sûr de vouloir supprimer le fournisseur "{selectedFournisseur?.nom}" ?
          </Typography>
          <Typography variant="body2" color="error" sx={{ mt: 2 }}>
            Attention: Cette action est irréversible. Assurez-vous qu'aucun produit n'est associé à ce fournisseur.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>
            Annuler
          </Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            startIcon={<DeleteIcon />}
          >
            Supprimer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Boutons d'export */}
      <Box display="flex" justifyContent="flex-end" gap={2} mt={4}>
        <Button
          variant="outlined"
          startIcon={<PrintIcon />}
          onClick={() => window.print()}
        >
          Imprimer
        </Button>
        <Button
          variant="outlined"
          startIcon={<DownloadIcon />}
          onClick={async () => {
            // TODO: Implémenter l'export Excel
            console.log('Export des fournisseurs');
          }}
        >
          Exporter Excel
        </Button>
      </Box>
    </Box>
  );
};

export default Fournisseurs;